import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import useSettings from "@/lib/useSettings";
import { useAuth } from "@/lib/auth";
import SectionHeader, { ScrollChevron } from "@/components/SectionHeader";
import Countdown from "@/components/Countdown";
import PRMBanner from "@/components/PRMBanner";
import { Calendar, Map, Flame } from "lucide-react";

const HERO_BG = "https://customer-assets.emergentagent.com/job_polski-hub-2/artifacts/m3v6jvnh_hero.jpg";

export default function Home() {
  const [gallery, setGallery] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const settings = useSettings();
  const { user } = useAuth();
  const cv = settings.countdown_visibility || "all";
  const countdownVisible = cv === "all" || (cv === "members" && !!user);

  useEffect(() => {
    const albumId = settings?.home_gallery_album;
    if (albumId === "__hide__") {
      setGallery([]);
      return;
    }
    const url = albumId ? `/gallery?album_id=${albumId}` : "/gallery";
    api.get(url).then((r) => setGallery(r.data.slice(0, 4))).catch(() => {});
    api.get("/events/upcoming?limit=1")
      .then((r) => {
        const upcoming = Array.isArray(r.data) ? r.data : [];
        if (upcoming[0]) setNextEvent(upcoming[0]);
      })
      .catch(() => {});
  }, [settings?.home_gallery_album]);

  return (
    <div data-testid="home-page">
      {/* HERO — full image always visible (no cropping) */}
      <section className="relative" style={{ background: "var(--sr-bg)" }}>
        <img
          src={HERO_BG}
          alt="Scythe Riders"
          className="block w-full h-auto"
          style={{ display: "block" }}
        />
        {/* dark gradient overlay for legibility */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(12,10,9,0.15) 0%, rgba(12,10,9,0.35) 60%, rgba(12,10,9,0.92) 100%)",
          }}
        />
        {/* title overlay anchored to bottom */}
        <div className="absolute inset-x-0 bottom-0 text-center px-4 pb-[6%]">
          <h1
            className="font-display font-bold fade-up"
            style={{
              color: "#fff5dd",
              textShadow: "0 2px 16px rgba(0,0,0,0.95), 0 0 30px rgba(255,106,26,0.5)",
              fontSize: "clamp(0.72rem, 3.1vw, 2.6rem)",
              whiteSpace: "nowrap",
              letterSpacing: "0.04em",
            }}
          >
            JEDNA DROGA <span className="text-[var(--sr-fire)]">•</span> JEDNA RODZINA{" "}
            <span className="text-[var(--sr-fire)]">•</span> JEDEN KLUB
          </h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 smoke-bottom pointer-events-none" />
      </section>

      <ScrollChevron />

      <PRMBanner />

      {/* COUNTDOWN — pokazuje się tylko jeśli istnieje nadchodzący zlot i admin nie ukrył */}
      {nextEvent && countdownVisible && (
        <section className="max-w-5xl mx-auto px-5 py-10 fade-up" data-testid="next-event-section">
          <Countdown targetDate={nextEvent.date} label={`DO ZLOTU: ${nextEvent.title.toUpperCase()}`} />
          <p className="text-center mt-5 font-nav uppercase tracking-widest text-xs" style={{ color: "var(--sr-muted)" }}>
            {nextEvent.location} • {new Date(nextEvent.date).toLocaleDateString("pl-PL", { day: "2-digit", month: "long", year: "numeric" })}
          </p>
        </section>
      )}

      {/* STATS — bez liczby członków */}
      <section className="max-w-6xl mx-auto px-5 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat icon={<Calendar />} value="2024" label="Rok założenia" />
          <Stat icon={<Map />} value={settings.stats_rallies || "—"} label="Zlotów i wypraw" />
          <Stat icon={<Flame />} value={settings.stats_km || "—"} label="Wspólnych km" />
        </div>
      </section>

      {/* O KLUBIE */}
      <section id="o-klubie" className="max-w-6xl mx-auto px-5 py-16">
        <SectionHeader title="O Klubie" />
        <div className="grid md:grid-cols-2 gap-10 items-center mt-10">
          <div className="fade-up">
            {(settings.about_intro || "")
              .split(/\n\s*\n/)
              .filter(Boolean)
              .map((para, i) => (
                <p
                  key={i}
                  className={i === 0 ? "text-lg leading-relaxed" : "mt-4 text-lg leading-relaxed"}
                  style={{ color: "var(--sr-cream-soft)" }}
                >
                  {para}
                </p>
              ))}
            <Link to="/o-klubie" className="inline-block mt-8" data-testid="about-cta">
              <span className="btn-fire">Czytaj więcej</span>
            </Link>
          </div>
          <div className="gallery-img fade-up delay-2" style={{ aspectRatio: "16/10" }} data-testid="home-about-image">
            {settings.home_about_image ? (
              <img src={resolveImageUrl(settings.home_about_image)} alt="Scythe Riders" />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center text-center p-8"
                style={{ background: "linear-gradient(135deg, rgba(255,106,26,0.10), rgba(12,10,9,0.8))", border: "1px solid var(--sr-border)" }}
              >
                <p className="font-nav uppercase tracking-widest text-xs" style={{ color: "var(--sr-muted)" }}>
                  Admin może wgrać tu zdjęcie<br/>w Panelu → Ustawienia
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* GALERIA */}
      {settings.home_gallery_album !== "__hide__" && (
        <section className="max-w-6xl mx-auto px-5 py-16">
          <SectionHeader title="Galeria" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {gallery.map((g, i) => (
              <div key={g.id} className={`gallery-img fade-up delay-${(i % 4) + 1}`} style={{ aspectRatio: "1/1" }} data-testid={`home-gallery-${i}`}>
                <img src={resolveImageUrl(g.image_url)} alt={g.caption || "Galeria"} />
              </div>
            ))}
            {gallery.length === 0 && (
              <p className="col-span-full text-center" style={{ color: "var(--sr-muted)" }}>Galeria wkrótce.</p>
            )}
          </div>
          <div className="text-center mt-10">
            <Link to="/galeria" data-testid="gallery-cta">
              <span className="btn-fire">Zobacz więcej zdjęć</span>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ icon, value, label }) {
  return (
    <div className="sr-card p-5 text-center fade-up" data-testid={`stat-${label}`}>
      <div className="text-[var(--sr-fire)] flex justify-center mb-2">{icon}</div>
      <div className="font-display text-3xl md:text-4xl text-[var(--sr-cream)]">{value}</div>
      <div className="font-nav text-[11px] md:text-xs uppercase tracking-widest mt-1" style={{ color: "var(--sr-muted)" }}>{label}</div>
    </div>
  );
}

function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}
